// proposal-batch-target-remap.ts
//
// H6 (audit Sol 2026-09-28): a `create` proposal names its ticket by `target`,
// but the controller assigns the real id (proposed-change-wire-mapping.ts:
// `nextTicketId` — a worker never picks its own id). A plan batch whose new
// tickets depend on EACH OTHER spells those dependencies with targets, so
// after allocation they pointed at ids that exist nowhere and
// `checkTicketDagForBatch` rejected the whole batch. These helpers let
// supervisor-proposal-application.ts rewrite such dependencies to the ids the
// targets were actually given, and refuse the one ambiguous case instead of
// guessing.

import type { AutopilotResult } from "./autopilot-result-schema.js";
import type { Ticket } from "./schemas/ticket-schema.js";

type WireChange = AutopilotResult["proposed_changes"][number];

/** One `create` of this batch: the worker's `target` and the id it was given. */
export interface CreatedInBatch {
  target: string;
  id: string;
}

function dependsOnOf(content: unknown): string[] {
  if (typeof content !== "object" || content === null) return [];
  const deps = (content as { depends_on?: unknown }).depends_on;
  return Array.isArray(deps) ? deps.filter((d): d is string => typeof d === "string") : [];
}

/**
 * Ambiguous targets are rejected with a reason rather than guessed: the same
 * target on two creates, or a create target equal to a LIVE ticket id that
 * the batch also depends on (the existing ticket, or the new one?). A target
 * colliding with a live id that nobody depends on is harmless.
 */
export function batchTargetCollisionReason(
  existing: Ticket[],
  changes: ReadonlyArray<WireChange>,
): string | undefined {
  const creates = changes.filter((c) => c.op === "create");
  // Two creates sharing a target make every dependency on it ambiguous (the
  // remap Map would silently keep the last one) — refuse, whether or not
  // anything depends on it yet.
  const seen = new Set<string>();
  for (const c of creates) {
    if (seen.has(c.target)) {
      return (
        `create target "${c.target}" xuất hiện nhiều lần trong cùng lô — ` +
        "mỗi ticket mới cần một target riêng để depends_on trỏ đúng"
      );
    }
    seen.add(c.target);
  }
  const referenced = new Set(creates.flatMap((c) => dependsOnOf(c.content)));
  const existingIds = new Set(existing.map((t) => t.id));
  const clash = creates.find((c) => existingIds.has(c.target) && referenced.has(c.target));
  if (clash === undefined) return undefined;
  return (
    `create target "${clash.target}" trùng id ticket đang có mà lô lại phụ thuộc vào nó — ` +
    "không rõ trỏ ticket cũ hay ticket mới; đặt target khác (vd T-01xx) cho ticket mới"
  );
}

/** Rewrites dependencies of this batch's NEW tickets from batch targets to assigned ids. */
export function remapBatchDependencies(tickets: Ticket[], created: CreatedInBatch[]): Ticket[] {
  if (created.length === 0) return tickets;
  const idByTarget = new Map(created.map((c) => [c.target, c.id]));
  const createdIds = new Set(created.map((c) => c.id));
  return tickets.map((t) =>
    createdIds.has(t.id) && t.depends_on.some((d) => idByTarget.has(d))
      ? { ...t, depends_on: t.depends_on.map((d) => idByTarget.get(d) ?? d) }
      : t,
  );
}
